import {
  Autocomplete,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  AdminTitle,
  AdminGroupsForm,
  ValidatorText,
  AdminUserDetails,
  AdminUserDetailsContainer,
  AdminUserGroups,
  AdminUserGroupsChip,
  AdminAutocompleteForm,
  AdminUserUpdateContainer,
  AdminModalInnerContainer,
  AdminModal,
  AdminFormGroup,
  AdminModalButtonContainer,
  AdminModalBody,
  SimpleTableContainer,
} from "../../admin.styled";
import { styled, useTheme } from "styled-components";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import PeopleIcon from "@mui/icons-material/People";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { useContext, useEffect, useState } from "react";
import { Group, Provider, User } from "../../admin.type";
import { Toast as showToast } from "core/components/global/toast/toast";
import {
  createGroup,
  getGroups,
  getProviders,
  updateGroup,
} from "modules/agentMySecurity/services/embeddingsService";
import { AuthContext } from "core/contexts/auth/auth";
import CloseIcon from "@mui/icons-material/Close";
import { group } from "console";
import AdvanceSearch from "core/components/global/advanceSearch/advanceSearch";
import AdminInvite from "../invites/invite";
import AdminGroup from "./group";
import NoResultsFound from "core/components/global/tables/noResults/noResultsFound";

// Define sort types
type SortColumn = "description" | "name" | "createdAt" | null;
type SortDirection = "asc" | "desc" | null;

export default function AdminGroups() {
  const theme = useTheme();
  const [name, setName] = useState<string>("");
  const [open, setOpen] = useState<boolean>(false);
  const [description, setDescription] = useState<string>("");
  const [providers, setProviders] = useState<any>([]);
  const [showGroupValidator, setShowGroupValidator] = useState<boolean>(false);
  const [showDescValidator, setShowDescValidator] = useState<boolean>(false);
  const { amsUser } = useContext(AuthContext);
  const [groups, setGroups] = useState<Group[]>([]);
  const [inputedProviders, setInputedProviders] = useState<{
    [key: string]: Provider[];
  }>({});
  const [copyOfSearchValue, setCopyOfSearchValue] = useState("");
  const [providerSearchQuery, setProviderSearchQuery] = useState("");
  const [filteredProviders, setFilteredProviders] = useState<Provider[]>([]);

  // Column sorting state
  const [sortColumn, setSortColumn] = useState<SortColumn>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [filteredGroups, setFilteredGroups] = useState<Group[] | null>(null);

  // Fetch the groups and providers from the backend
  useEffect(() => {
    async function fetchAdGroups() {
      try {
        const response = await getGroups();
        const groups = response.map((group) => {
          // Map over group.providers to get the full provider objects
          const updatedProviders = group.providers.map((providerIdOrName) => {
            // Try to find by name first
            let provider = providers.find((p) => p.name === providerIdOrName);
            // If not found by name, try by ID
            if (!provider) {
              provider = providers.find((p) => p._id === providerIdOrName);
            }
            return provider;
          });

          // Return the group with the updated providers
          return {
            ...group,
            providers: updatedProviders,
          };
        });

        setGroups(groups);
      } catch (error) {
        setGroups([]);
      }
    }

    if (providers.length > 0) {
      fetchAdGroups();
    }
  }, [providers]);

  useEffect(() => {
    async function fetchProviders() {
      try {
        const responses = await getProviders();

        // Filter out duplicates by name and only include active providers
        const uniqueProviders = Array.isArray(responses) 
          ? responses.reduce((acc, provider) => {
              // Only include providers that are active (use === true)
              if (provider.options && provider.options.use === true) {
                // Check if we already have a provider with this name
                const existingIndex = acc.findIndex(p => p.name === provider.name);
                if (existingIndex === -1) {
                  // If no duplicate found, add to accumulator
                  acc.push(provider);
                }
              }
              return acc;
            }, [])
          : [];
        
        // Sort the unique providers alphabetically by name
        const sortedProviders = [...uniqueProviders].sort((a, b) => 
          a.name.localeCompare(b.name)
        );
          
        setProviders(sortedProviders);
      } catch (error) {
        setProviders([]);
      }
    }

    fetchProviders();
  }, []);

  // Reset the description and name when the modal is closed
  useEffect(() => {
    if (open === false) {
      setName("");
      setDescription("");
    }
  }, [open]);

  // Remotely update the list of groups
  const handleUpdateGroup = async (updatedGroup: Group) => {
    // Update the user's groups
    if (updatedGroup) {
      // Post the updated group
      const response = await updateGroup({
        providers: updatedGroup.providers,
        name: updatedGroup.name,
        description: updatedGroup.description,
        groupId: updatedGroup._id,
      });

      if (response !== null) {
        setOpen(false);
        showToast(response.message, "success");

        const updatedInvites = groups.map((group) =>
          group._id === updatedGroup._id
            ? {
                ...group,
                ...updatedGroup,
              }
            : group
        );

        setGroups(updatedInvites);
      }
    }
  };

  // Remotely update the list of groups (close icon)
  const handleRemoveNewGroup = (
    groupName: string,
    providerNameToRemove: string
  ) => {
    // Update the curent inputed state
    setInputedProviders((prevState) => ({
      ...prevState,
      // Once the prevState[user._id] has been initiated it will always be valid even with a count of 0 groups
      [groupName]: inputedProviders[groupName].filter(
        (provider) => provider.name !== providerNameToRemove
      ),
    }));
  };

  function handleUpdateNewGroup(event, provider) {
    // if (!event.target.name) {
    //   setInputedProviders((prevState) => ({
    //     ...prevState,
    //     ["000000000000000"]: provider,
    //   }));
    // } else if (event.target.name === "name") {
    //   setName(event.target.value);
    // } else if (event.target.name === "description") {
    //   setDescription(event.target.value);
    // }
    const { name, value } = event.target;
    if (!name) {
    setInputedProviders((prevState) => ({
      ...prevState,
      ["000000000000000"]: provider,
    }));
  } else if (name === "name") {
    if (value.length <= 2000) {
      setName(value);
      // setShowGroupValidator(false); // hide validator if previously shown
    } 
  } else if (name === "description") {
    if (value.length <= 2000) {
      setDescription(value);
    } 
  }

  }
  
  // Handle provider selection in the table
  const handleProviderSelection = (provider: Provider) => {
    setInputedProviders(prevState => {
      const currentProviders = prevState["000000000000000"] || [];
      const isSelected = currentProviders.some(p => p.name === provider.name);
      
      if (isSelected) {
        // Remove provider if already selected
        return {
          ...prevState,
          ["000000000000000"]: currentProviders.filter(p => p.name !== provider.name)
        };
      } else {
        // Add provider if not selected
        return {
          ...prevState,
          ["000000000000000"]: [...currentProviders, provider]
        };
      }
    });
  };
  
  // Check if a provider is selected
  const isProviderSelected = (provider: Provider) => {
    const currentProviders = inputedProviders["000000000000000"] || [];
    return currentProviders.some(p => p.name === provider.name);
  };
  
  // Handle select all providers
  const handleSelectAllProviders = () => {
    setInputedProviders(prevState => ({
      ...prevState,
      ["000000000000000"]: [...filteredProviders]
    }));
  };
  
  // Handle clear all providers
  const handleClearAllProviders = () => {
    setInputedProviders(prevState => ({
      ...prevState,
      ["000000000000000"]: []
    }));
  }

  async function handleSubmitNewGroup() {
    if (name === "" || name.length === 1) {
      setShowGroupValidator(true);
    }
    if (description === "") {
      setShowDescValidator(true);
    }

    if (name !== "" && description !== "" && name.length > 1) {
      // Extract just the provider names from the selected providers
      const selectedProviderNames = (inputedProviders["000000000000000"] || []).map(provider => provider.name);
      
      // Create provider objects that match the expected type
      const providersForAPI = selectedProviderNames.map(name => ({
        name,
        _id: "", // Placeholder, will be ignored by backend
        module: "",
        options: {
          description: "",
          nice_name: "",
          access: [],
          models: [],
          use: true,
          accepts_files: false,
          accepts_text: true,
          org: "",
          _id: ""
        },
        createdAt: "",
        updatedAt: "",
        __v: 0
      }));
      
      const response = await createGroup({
        name: name,
        description: description,
        providers: selectedProviderNames as any, // Type cast to satisfy TypeScript
      });

      setOpen(false);

      if (response !== null) {
        showToast(response.message, "success");
        
        // Refresh the groups list by fetching the latest data
        const updatedGroups = await getGroups();
        if (updatedGroups) {
          // Map over group.providers to get the full provider objects
          const groupsWithProviders = updatedGroups.map((group) => {
            const updatedProviders = group.providers.map((providerIdOrName) => {
              // Try to find by name first
              let provider = providers.find((p) => p.name === providerIdOrName);
              // If not found by name, try by ID
              if (!provider) {
                provider = providers.find((p) => p._id === providerIdOrName);
              }
              return provider;
            });

            // Return the group with the updated providers
            return {
              ...group,
              providers: updatedProviders,
            };
          });
          
          setGroups(groupsWithProviders);
        }
      } else {
        showToast("Unable to create a group at this time", "error");
      }
    }
  }

  /**
   * Apply sorting whenever sort parameters change
   */
  // Filter providers based on search query
  useEffect(() => {
    if (providers.length > 0) {
      if (providerSearchQuery.trim() === "") {
        setFilteredProviders(providers);
      } else {
        const filtered = providers.filter(provider => 
          provider.name.toLowerCase().includes(providerSearchQuery.toLowerCase())
        );
        setFilteredProviders(filtered);
      }
    }
  }, [providerSearchQuery, providers]);

  useEffect(() => {
    if (!sortColumn || !sortDirection) {
      return; // No sorting applied
    }

    // Apply sorting to the data that's currently displayed
    // (either filtered or all data)
    const dataToSort = [...(filteredGroups === null ? groups : filteredGroups)];

    const sortedData: any = dataToSort.sort((a, b) => {
      // Default comparison for undefined values
      if (a[sortColumn] === undefined && b[sortColumn] === undefined) return 0;
      if (a[sortColumn] === undefined) return 1;
      if (b[sortColumn] === undefined) return -1;

      let comparison = 0;

      // Special handling for dates
      if (sortColumn === "createdAt") {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        comparison = dateA - dateB;
      }
      // String comparison for other fields
      else {
        const valueA = String(a[sortColumn]).toLowerCase();
        const valueB = String(b[sortColumn]).toLowerCase();

        if (valueA < valueB) {
          comparison = -1;
        } else if (valueA > valueB) {
          comparison = 1;
        }
      }

      // Reverse for descending order
      return sortDirection === "desc" ? -comparison : comparison;
    });

    // Update the appropriate state based on whether we're sorting filtered or all data
    if (filteredGroups === null) {
      setGroups(sortedData);
    } else {
      setFilteredGroups(sortedData);
    }
  }, [sortColumn, sortDirection]);

  /**
   * Handle column header click to sort
   */
  const handleSort = (column: SortColumn) => {
    // If clicking the same column, toggle direction
    if (sortColumn === column) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else if (sortDirection === "desc") {
        setSortDirection("asc");
      }
    } else {
      // New column, start with ascending
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const handleFilterData = (newKeys, b, c) => {
    console.log("handleFilterData");
    setFilteredGroups(newKeys);
    // Reset sorting when search changes
    setSortColumn(null);
    setSortDirection(null);
  };

  const handleClearFilter = () => {
    setFilteredGroups(null);
  };

  /**
   * Get sort icon for column header
   */
  const getSortIcon = (column: SortColumn) => {
    if (sortColumn !== column) {
      return null;
    }

    return sortDirection === "asc" ? (
      <ArrowUpwardIcon fontSize="small" className="ms-1" />
    ) : (
      <ArrowDownwardIcon fontSize="small" className="ms-1" />
    );
  };

  const handleDeleteGroup = (groupId: string) => {
    setGroups((prev) => prev.filter((grp) => grp._id !== groupId));
    setFilteredGroups((prev) =>
      prev ? prev.filter((grp) => grp._id !== groupId) : null
    );
  };

  return (
    <>
      <SimpleTableContainer elevation={1} columns={3}>
        {/* Data Search */}
        <div className="pb-3 d-flex justify-content-between align-items-center">
          <h5>Active Directory Groups</h5>
          <AdvanceSearch
            initialData={groups}
            setFilteredData={handleFilterData}
            clearFilteredData={handleClearFilter}
            setCopyOfSearchValue={setCopyOfSearchValue}
          />
        </div>

        {/* Collapsable Table */}
        <div aria-label="collapsible table">
          {/* Headers */}
          <div
            style={{ borderRadius: "5px 5px 0 0" }}
            className="outer-header-container"
          >
            <div className="row-items">
              <p
                className="header-item d-flex align-items-center"
                onClick={() => handleSort("name")}
                style={{ cursor: "pointer" }}
              >
                Name
                {getSortIcon("name")}
              </p>
              <p
                className="header-item d-flex align-items-center"
                onClick={() => handleSort("description")}
                style={{ cursor: "pointer" }}
              >
                Description
                {getSortIcon("description")}
              </p>
              <p
                className="header-item d-flex align-items-center"
                onClick={() => handleSort("createdAt")}
                style={{ cursor: "pointer" }}
              >
                Created At
                {getSortIcon("createdAt")}
              </p>
            </div>
            <div className="row-buttons">
              <IconButton
                aria-label="send prompt"
                onClick={() => setOpen(true)}
                edge="end"
                className="me-1 icon-button"
              >
                <AddIcon />
              </IconButton>
            </div>
          </div>

          {/* Body */}
          <div
            style={{ borderRadius: "0 0 5px 5px" }}
            className="outer-row-container"
          >
            {(filteredGroups === null ? groups : filteredGroups).length > 0 ? (
              
              filteredGroups === null ? (
                <>
                  {groups.map((group) => (
                    // Row items
                    <AdminGroup
                      key={`row-${group._id}`}
                      group={group}
                      availableProviders={providers}
                      handleUpdateGroup={handleUpdateGroup}
                      onDelete={handleDeleteGroup}
                    />
                  ))}
                </>
              ) : (
                <>
                  {filteredGroups.map((group) => (
                    // Row items
                    <AdminGroup
                      key={`row-${group._id}`}
                      group={group}
                      availableProviders={providers}
                      handleUpdateGroup={handleUpdateGroup}
                      onDelete={handleDeleteGroup}
                    />
                  ))}
                </>
              )
            ) : (
              // If no results, show the NoResultsFound component
              <NoResultsFound
                searchTerm={filteredGroups !== null ? copyOfSearchValue : ""}
              />
            )}
          </div>
        </div>
      </SimpleTableContainer>

      {/* Modal */}
      <AdminModal
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <AdminModalInnerContainer elevation={2}>
          <AdminTitle>
            <div className="d-flex">
              <p className="ps-1">Create a Group</p>
            </div>
          </AdminTitle>
          <AdminModalBody>
            <AdminGroupsForm>
              <AdminFormGroup>
                <label>Group Name</label>
                <input
                  onChange={(event) => handleUpdateNewGroup(event, "")}
                  placeholder="Group Name"
                  type="text"
                  className="custom-input"
                  name="name"
                  aria-placeholder="Enter group name"
                  style={{ color: theme.colors.white }}
                  maxLength={2000}
                />
                {showGroupValidator && (
                  <ValidatorText>
                    <p>A group can't be empty</p>
                    <p>A group must have 2 or more characters</p>
                  </ValidatorText>
                )}
              </AdminFormGroup>
              <AdminFormGroup>
                <label>Description</label>
                <textarea
                  onChange={(event) => handleUpdateNewGroup(event, "")}
                  placeholder="Description"
                  className="form-control autoForm"
                  name="description"
                  aria-placeholder="Describe the group"
                  style={{ color: theme.colors.white }}
                  maxLength={2000}
                />
                {showDescValidator && (
                  <ValidatorText>
                    <p>A description can't be empty</p>
                  </ValidatorText>
                )}
              </AdminFormGroup>
              <AdminFormGroup>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                  <label style={{ marginBottom: 0, marginRight: '8px' }}>Providers</label>
                  <span style={{ 
                    backgroundColor: theme.colors.sidebarcolor, 
                    color: theme.colors.white, 
                    borderRadius: '16px', 
                    padding: '2px 8px', 
                    fontSize: theme.font.small, 
                    fontWeight: 500,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '24px',
                    height: '20px'
                  }}>
                    {(inputedProviders["000000000000000"] || []).length}
                  </span>
                </div>
                
                
                {/* Provider selection table */}
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    {/* Search box */}
                    <TextField
                      placeholder="Search providers..."
                      variant="outlined"
                      size="small"
                      value={providerSearchQuery}
                      onChange={(e) => setProviderSearchQuery(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start" className="ms-2">
                            <SearchIcon style={{ color: theme.colors.white }}/>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        flexGrow: 1,
                        margin: 0,
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: theme.colors.light,
                          borderRadius: '4px',
                          margin: 0,
                          padding: 0,
                          height: '37.5px',
                          '& fieldset': {
                            borderColor: 'transparent',
                            borderWidth: 0,
                          },
                          '&:hover fieldset': {
                            borderColor: 'transparent',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: 'transparent',
                            borderWidth: 0,
                          },
                        },
                        '& .MuiInputBase-input': {
                          color: theme.colors.white,
                          fontSize: theme.font.h4,
                          padding: '5px 12px',
                          height: '24px',
                          border: "none"
                        },
                        '& .MuiInputBase-input:active': {
                          border: "none"
                        },
                      }}
                    />
                    
                    {/* Bulk action buttons */}
                    <div className="d-flex gap-2 ms-2">

                        <Button variant="contained" onClick={handleSelectAllProviders}>
                          Select All
                        </Button>

                        <Button variant="contained" onClick={handleClearAllProviders}>
                          Clear All
                        </Button>

                    </div>
                  </div>
                  
                  {/* Provider table */}
                  <TableContainer
                    component={Paper}
                    style={{ 
                      maxHeight: '300px', 
                      overflowY: 'auto',
                      borderRadius: theme.borders.borderRadius,
                      boxShadow: 'none',
                      backgroundColor: theme.name === 'light-theme' ? theme.colors.white : theme.colors.tableBackground
                    }}
                  >
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell 
                            padding="checkbox"
                            style={{
                              backgroundColor: theme.name === 'light-theme' ? '#F5F5F5' : theme.colors.mediumGrey,
                              borderBottom: `1px solid ${theme.colors.lightGrey}`
                            }}
                          >
                            <Checkbox
                              indeterminate={
                                (inputedProviders["000000000000000"] || []).length > 0 &&
                                (inputedProviders["000000000000000"] || []).length < filteredProviders.length
                              }
                              checked={
                                filteredProviders.length > 0 &&
                                (inputedProviders["000000000000000"] || []).length === filteredProviders.length
                              }
                              onChange={e => {
                                if (e.target.checked) {
                                  handleSelectAllProviders();
                                } else {
                                  handleClearAllProviders();
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell
                            style={{
                              backgroundColor: theme.name === 'light-theme' ? '#F5F5F5' : theme.colors.mediumGrey,
                              borderBottom: `1px solid ${theme.colors.lightGrey}`,
                              fontSize: theme.font.h4,
                              fontWeight: 600,
                              color: theme.name === 'light-theme' ? theme.colors.textColor : theme.colors.white,
                              padding: '12px 16px'
                            }}
                          >
                            Provider Name
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredProviders.length > 0 ? (
                          filteredProviders.map((provider) => (
                            <TableRow 
                              key={provider.name}
                              selected={isProviderSelected(provider)}
                              onClick={() => handleProviderSelection(provider)}
                              style={{ 
                                cursor: 'pointer',
                                backgroundColor: isProviderSelected(provider) 
                                  ? theme.colors.historyItemselect 
                                  : theme.name === 'light-theme' ? theme.colors.white : theme.colors.tableBackground,
                                borderBottom: `1px solid ${theme.colors.lightGrey}`
                              }}
                              hover
                              sx={{
                                '&:hover': {
                                  backgroundColor: theme.colors.historyItemhvr,
                                },
                              }}
                            >
                              <TableCell 
                                padding="checkbox"
                                style={{
                                  padding: '10px 0 10px 16px',
                                  borderBottom: `1px solid ${theme.colors.lightGrey}`
                                }}
                              >
                                <Checkbox 
                                  checked={isProviderSelected(provider)}
                                  style={{
                                    color: theme.colors.sidebarcolor
                                  }}
                                />
                              </TableCell>
                              <TableCell
                                style={{
                                  fontSize: theme.font.h4,
                                  color: theme.colors.textColor,
                                  padding: '12px 16px',
                                  borderBottom: `1px solid ${theme.colors.lightGrey}`
                                }}
                              >
                                {provider.name}
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell 
                              colSpan={2} 
                              align="center"
                              style={{
                                padding: '16px',
                                color: theme.colors.textDisabledColor,
                                fontSize: theme.font.h4,
                                borderBottom: 'none'
                              }}
                            >
                              {providerSearchQuery ? 'No matching providers found' : 'No providers available'}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>
              </AdminFormGroup>
            </AdminGroupsForm>
          </AdminModalBody>
          <AdminModalButtonContainer>
            <Button variant="contained" onClick={() => handleSubmitNewGroup()}>
              Create Group
            </Button>
          </AdminModalButtonContainer>
        </AdminModalInnerContainer>
      </AdminModal>
    </>
  );
}
